/**
 * JWT `personas` is stored as a comma-separated string in localStorage.
 * Use token-level checks instead of substring matching on the whole string.
 */

export const PERSONA_MEMBER = 'member';
export const PERSONA_EMPLOYEE = 'employee';
export const PERSONA_LOAN_HOLDER = 'loan-holder';
export const PERSONA_APPLICANT = 'applicant';

const FALLBACK_DEFAULT_ROUTE = '/portal/account';

export function parsePersonas(personas: string | null): string[] {
  if (!personas) return [];
  return personas
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export function hasPersona(personas: string | null, name: string): boolean {
  return parsePersonas(personas).includes(name);
}

/**
 * Hub (membership drawer) access: member or employee.
 * `null` = personas key missing (legacy token before claims existed) → allow Hub
 * until refresh writes claims. Empty string / non-Hub personas → deny.
 */
export function canAccessHub(personas: string | null): boolean {
  if (personas === null) {
    return true;
  }
  const tokens = parsePersonas(personas);
  return tokens.includes(PERSONA_MEMBER) || tokens.includes(PERSONA_EMPLOYEE);
}

export function getDefaultRoute(): string {
  const route = localStorage.getItem('default_route');
  if (route && route.startsWith('/')) {
    return route;
  }
  return FALLBACK_DEFAULT_ROUTE;
}

/**
 * Resolve post-login / post-password-change destination.
 * Usable same-origin returnUrl wins; bare `/` and `/login` fall back to default_route.
 */
export function resolvePostLoginDestination(
  rawReturnUrl: string | null,
  defaultRoute: string | null | undefined
): string {
  const fallback =
    defaultRoute && defaultRoute.startsWith('/') ? defaultRoute : '/';

  let returnUrl = rawReturnUrl || fallback;

  if (returnUrl === '/' || returnUrl === '') {
    returnUrl = fallback;
  }

  try {
    const url = new URL(returnUrl, window.location.origin);
    const pathname = url.pathname.toLowerCase();
    if (pathname === '/login') {
      return fallback;
    }
    if (url.origin !== window.location.origin) {
      return fallback;
    }
    if (pathname === '/' && !url.search && !url.hash) {
      return fallback;
    }
    return `${url.pathname}${url.search}${url.hash}` || fallback;
  } catch {
    const pathnameMatch = returnUrl.match(/^([^?#]+)/);
    if (pathnameMatch && pathnameMatch[1].toLowerCase() === '/login') {
      return fallback;
    }
    if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
      return fallback;
    }
    return returnUrl || fallback;
  }
}
